import QRCode from "qrcode";
import jsQR from "jsqr";
import { compressSync, decompressSync, strFromU8, strToU8 } from "fflate";

export const CREDENTIAL_PREFIX = "PCS3:";
export const CREDENTIAL_STRIP_HEIGHT = 380;
export const CREDENTIAL_MIN_WIDTH = 680;

export function encodeCredential(manifest) {
  const compact = {
    s: 3,
    t: manifest.taskId,
    c: [manifest.canvas.width, manifest.canvas.height],
    p: [manifest.photoArea.width, manifest.photoArea.height],
    i: manifest.images.map((image) => [
      image.x, image.y, image.width, image.height,
      image.originalName, image.outputName, image.sourceFormat || "",
    ]),
  };
  return CREDENTIAL_PREFIX + toBase64Url(compressSync(strToU8(JSON.stringify(compact)), { level: 9 }));
}

export function decodeCredential(payload) {
  if (!payload?.startsWith(CREDENTIAL_PREFIX)) throw new Error("图片凭证标识无效。");
  const compact = JSON.parse(strFromU8(decompressSync(fromBase64Url(payload.slice(CREDENTIAL_PREFIX.length)))));
  if (compact.s !== 3 || !Array.isArray(compact.c) || !Array.isArray(compact.p) || !Array.isArray(compact.i)) {
    throw new Error("图片凭证内容无效。");
  }
  return {
    schema: "pixel-cake-stitcher/v3",
    taskId: compact.t,
    canvas: { width: compact.c[0], height: compact.c[1] },
    photoArea: { width: compact.p[0], height: compact.p[1] },
    credential: { type: "qr-strip", height: compact.c[1] - compact.p[1] },
    images: compact.i.map((item, index) => ({
      index,
      x: item[0], y: item[1], width: item[2], height: item[3],
      originalName: item[4], outputName: item[5], sourceFormat: item[6],
    })),
    fileName: "从图片内自动读取的凭证",
  };
}

export async function drawCredentialStrip(context, manifest) {
  const payload = encodeCredential(manifest);
  const qrModel = QRCode.create(payload, { errorCorrectionLevel: "H" });
  const moduleCount = qrModel.modules.size + 8;
  const moduleScale = Math.max(3, Math.floor(320 / moduleCount));
  const qrSize = moduleCount * moduleScale;
  const qr = document.createElement("canvas");
  await QRCode.toCanvas(qr, payload, {
    errorCorrectionLevel: "H",
    margin: 4,
    width: qrSize,
    color: { dark: "#111111", light: "#ffffff" },
  });

  const top = manifest.photoArea.height;
  context.fillStyle = "#ffffff";
  context.fillRect(0, top, manifest.canvas.width, CREDENTIAL_STRIP_HEIGHT);
  context.fillStyle = "#111111";
  context.font = "700 20px system-ui, sans-serif";
  context.textAlign = "center";
  context.fillText("照片拼合还原凭证 · 请勿裁剪底部", manifest.canvas.width / 2, top + 31);
  context.font = "14px system-ui, sans-serif";
  context.fillStyle = "#555555";
  context.fillText(manifest.taskId, manifest.canvas.width / 2, top + 54);

  const qrTop = top + 58;
  if (manifest.canvas.width >= 980) {
    context.drawImage(qr, Math.round(manifest.canvas.width * 0.25 - qr.width / 2), qrTop);
    context.drawImage(qr, Math.round(manifest.canvas.width * 0.75 - qr.width / 2), qrTop);
  } else {
    context.drawImage(qr, Math.round((manifest.canvas.width - qr.width) / 2), qrTop);
  }
  return payload.length;
}

export async function readCredentialFromBitmap(bitmap) {
  const scanHeight = Math.min(bitmap.height, 460);
  const cropWidth = Math.min(bitmap.width, 440);
  const centers = bitmap.width >= 980
    ? [bitmap.width * 0.25, bitmap.width * 0.75, bitmap.width * 0.5]
    : [bitmap.width * 0.5];
  for (const center of centers) {
    const x = Math.max(0, Math.min(bitmap.width - cropWidth, Math.round(center - cropWidth / 2)));
    const canvas = document.createElement("canvas");
    canvas.width = cropWidth;
    canvas.height = scanHeight;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(bitmap, x, bitmap.height - scanHeight, cropWidth, scanHeight, 0, 0, cropWidth, scanHeight);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const result = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "attemptBoth" });
    if (result?.data?.startsWith(CREDENTIAL_PREFIX)) return decodeCredential(result.data);
  }
  return null;
}

function toBase64Url(bytes) {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(base64);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
